import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button, Input, Divider } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { useAuth } from '@/auth/AuthProvider';
import { verifySsoMfa } from '@/api/auth-flow';
import { ssoSessionFromTokens, SSO_INTENDED_KEY, SSO_VERIFIER_KEY } from '@/lib/sso';

interface Props {
  /** Held SSO session returned by the callback when the account requires MFA. */
  sessionId: string;
  /** Where to land once MFA passes (the originally requested URL). */
  intendedUrl: string;
  onBack: () => void;
}

/**
 * Second factor for SSO logins. Mirrors {@link TotpVerificationForm} but uses
 * the held `session_id` (no email/password) to exchange the OTP for the tokens
 * the backend withheld at the callback step.
 */
export function SsoMfaVerificationForm({ sessionId, intendedUrl, onBack }: Props) {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [useBackup, setUseBackup] = useState(false);
  const [otp, setOtp] = useState('');
  const [backup, setBackup] = useState('');

  const { mutateAsync, isPending } = useMutation({ mutationFn: verifySsoMfa });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      // The verify endpoint accepts the authenticator OR a backup code, both in
      // the `otp_code` field.
      const result = await mutateAsync({
        session_id: sessionId,
        otp_code: useBackup ? backup : otp,
      });
      if (!result?.access_token) {
        setError('Invalid response from server. Please try again.');
        return;
      }
      const session = ssoSessionFromTokens({
        access_token: result.access_token,
        refresh_token: result.refresh_token,
        exp: result.exp,
      });
      if (setSession(session, 'sso')) {
        sessionStorage.removeItem(SSO_VERIFIER_KEY);
        sessionStorage.removeItem(SSO_INTENDED_KEY);
        navigate(intendedUrl, { replace: true });
      } else {
        setError('Invalid token returned. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed.');
    }
  };

  const disabled = isPending || (useBackup ? !backup : otp.length !== 6);

  return (
    <form onSubmit={submit} style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {error ? <Alert tone="danger" title={error} /> : null}

      {useBackup ? (
        <Field label="Backup code" required>
          {(p) => (
            <Input
              {...p}
              value={backup}
              onChange={(e) => setBackup(e.target.value.toUpperCase())}
              placeholder="XXXX-XXXX"
              autoComplete="one-time-code"
            />
          )}
        </Field>
      ) : (
        <Field label="Authentication code" required description="6-digit code from your authenticator app.">
          {(p) => (
            <Input
              {...p}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              placeholder="000000"
              autoComplete="one-time-code"
            />
          )}
        </Field>
      )}

      <div style={{ textAlign: 'right', fontSize: 'var(--vn-font-size-md)' }}>
        <button
          type="button"
          onClick={() => {
            setUseBackup((v) => !v);
            setError(null);
          }}
          style={{
            background: 'none',
            border: 0,
            cursor: 'pointer',
            color: 'var(--vn-primary-solid)',
            textDecoration: 'underline',
          }}
        >
          {useBackup ? 'Use authenticator app' : 'Use a backup code'}
        </button>
      </div>

      <Button type="submit" variant="primary" fullWidth disabled={disabled} loading={isPending}>
        Verify
      </Button>

      <Divider>or</Divider>

      <Button type="button" variant="secondary" fullWidth onClick={onBack}>
        Use another account
      </Button>
    </form>
  );
}
